import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import EscalationControl from '../../customer/components/EscalationControl';
import WorkflowAccessSteps, { StepAccessGap } from './WorkflowAccessSteps';
import type { useWorkflowAccess } from '../hooks/useWorkflowAccess';
import type { StepAccess, WorkflowCatalogEntry, WorkflowRunState } from '../../../types/backend';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The workflow already chosen. The dialog never offers a second way to pick one. */
  workflow: WorkflowCatalogEntry | null;
  catalogueError: string | null;
  onStart: (workflowName: string) => Promise<WorkflowRunState | void>;
  /** The preflight. Omitted, the launcher starts without one and the backend alone refuses. */
  access?: ReturnType<typeof useWorkflowAccess>;
  /** The customer the operator escalates on when a workflow needs more than they have. */
  customerId?: string;
}

const LEVEL_WORDS: Record<string, string> = { l1: 'L1', l2: 'L2', admin: 'Admin', none: 'no access' };

// Starting by name here and asking the agent in chat both reach investigations:StartWorkflow, so
// there is one entry point rather than two code paths that can disagree.
//
// The dialog is opened by whoever owns the procedure controls, not by a button of its own: an
// investigation has one place to start anything from, and a second trigger here would be a second
// visual language for the same act.
//
// A workflow never escalates. The preflight says before Start which steps the operator's access
// covers; when a step every run reaches is not covered, Start waits until they escalate themselves.
const WorkflowStartDialog = memo<Props>(({
  open,
  onClose,
  workflow,
  catalogueError,
  onStart,
  access,
  customerId,
}) => {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set after a start that succeeded with steps some route reaches and the operator cannot do.
  const [warnings, setWarnings] = useState<StepAccess[] | null>(null);

  const choice = workflow;
  const report = choice ? (access?.report ?? null) : null;
  // Only a report that says startable opens Start. A check that failed, is running, or never ran
  // is an unknown, and an unknown is not a yes (PRIN-02).
  const blocked = Boolean(choice && access && (access.checking || !report?.startable));
  const firstGap = report?.steps.find((s) => s.mandatory && !s.permitted);

  // The escalation control reports the operator's level on load and on every change. Only a change
  // re-runs the check: comparing against the report's level would loop wherever the two words
  // differ, such as an operator with no ceiling whom the control reports as L1.
  const seenLevel = useRef<string | null>(null);
  const recheck = access?.recheck;
  // Memoised: the escalation control re-runs its effect whenever this callback's identity changes.
  const onLevelChange = useCallback(
    (level: string) => {
      if (seenLevel.current !== null && seenLevel.current !== level) void recheck?.();
      seenLevel.current = level;
    },
    [recheck],
  );

  // The check runs on what the dialog was opened with, not on a selection made inside it: the
  // choosing happens in the one picker now, and a preflight for a workflow nobody picked would
  // be a check with no subject.
  const check = access?.check;
  useEffect(() => {
    if (open) check?.(workflow?.name ?? null);
  }, [open, workflow, check]);

  const close = () => {
    // Reopening starts clean: an error about a start that is over belongs to the dialog that
    // was closed.
    onClose();
    setError(null);
    setWarnings(null);
  };

  const handleStart = async () => {
    if (!choice) return;
    setStarting(true);
    setError(null);
    try {
      const run = await onStart(choice.name);
      const gaps = run?.access_warnings ?? [];
      if (gaps.length > 0) {
        setWarnings(gaps);
      } else {
        close();
      }
    } catch (e) {
      // Without this the rejection is unhandled, the dialog stays open looking idle, and the
      // operator cannot tell a refused start from a slow one.
      setError(e instanceof Error ? e.message : 'Could not start that workflow');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Run a workflow</DialogTitle>
        {warnings ? (
          <>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Started. These steps need access you do not have; the run stops at one only if
                its route reaches it.
              </Alert>
              <Stack spacing={1}>
                {warnings.map((step) => (
                  <StepAccessGap key={step.node_id} step={step} />
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={close}>
                Done
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              {catalogueError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {catalogueError}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {choice ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{choice.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    v{choice.version} · {choice.node_count} steps · {choice.layer_count} stages
                    {choice.description ? ` · ${choice.description}` : ''}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">No workflow chosen.</Typography>
              )}
              {choice && access && (
                <Box sx={{ mt: 2 }}>
                  {access.checking && !report && (
                    <Stack direction="row" spacing={1} alignItems="center" role="status" aria-live="polite">
                      <CircularProgress size={16} />
                      <Typography variant="body2">Checking your access</Typography>
                    </Stack>
                  )}
                  {access.error && (
                    <Alert
                      severity="error"
                      action={
                        <Button size="small" color="inherit" onClick={() => void access.recheck()}>
                          Check again
                        </Button>
                      }
                    >
                      {access.error}
                    </Alert>
                  )}
                  {report && (
                    <Stack spacing={1.5}>
                      <Typography variant="body2">
                        Your access on this customer: {LEVEL_WORDS[report.effective_level] ?? report.effective_level}
                      </Typography>
                      {!report.startable && (
                        <Alert severity="error">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            You cannot start this workflow: {firstGap?.label ?? 'a step'} needs access you
                            do not have.
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Escalate yourself here and the check runs again, or ask for the workflow to
                            be changed.
                          </Typography>
                          {customerId && (
                            <Box sx={{ mt: 1 }}>
                              <EscalationControl
                                customerId={customerId}
                                onEffectiveLevelChange={onLevelChange}
                              />
                            </Box>
                          )}
                        </Alert>
                      )}
                      <WorkflowAccessSteps steps={report.steps} />
                    </Stack>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={close}>Cancel</Button>
              <Button variant="contained" disabled={!choice || starting || blocked} onClick={handleStart}>
                Start
              </Button>
            </DialogActions>
          </>
        )}
    </Dialog>
  );
});

WorkflowStartDialog.displayName = 'WorkflowStartDialog';

export default WorkflowStartDialog;
